import { useContext, useEffect, useMemo, useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Slider,
  Stack,
  Typography,
} from '@mui/material';

import { LayerContext } from 'state/LayerContext';
import { useGetSegmentInfoQuery } from 'state/apiSlice';
import {
  selectScenarioCapacity,
  selectScenarioLength,
  selectScenarioSegments,
  setScenarioCapacity,
} from 'state/detourSlices';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  selectCurrentProjectInfo,
  selectSegmentId,
  setRouteSelection,
  setSelectedSegmentId,
} from 'state/workflowSlice';

import { isMac } from 'appUtils';
import { InfoAlert } from 'features/common/Alerts';
import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import { classNames, formatDistance } from 'features/common/utils';
import { InfoWalkmeCrumb } from 'features/info_hover/InfoHover';
import UndoSpan from 'features/map/UndoSpan';
import ZoomToRoute from 'features/map/overlays/ZoomToRoute';
import LoadingDotAnimation from 'features/spinner/LoadingDotAnimation';
import { Geometry } from 'geojson';

import { useSelectedScenarioClosureRoute } from '../ScenarioDataProvider';
import { useSegmentRouteInfo } from './useSegmentRouteInfo';

import mapStyles from '../../map/Map.module.css';
import styles from './DetourMapTooltip.module.css';

const INFO_TXT = 'Select a segment on the map';

interface IDetourMapTooltip {
  viewOnly: boolean;
}

export default function DetourMapTooltip({ viewOnly }: IDetourMapTooltip) {
  const [isOpen, setIsOpen] = useState<boolean>(true);

  const selectedSegment = useAppSelector(selectSegmentId);
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const scenarioLength = useAppSelector(selectScenarioLength);

  const selectionFeature = useSegmentRouteInfo();
  const { currentData: scenarioRouteData } = useSelectedScenarioClosureRoute();

  const infoTxt = useMemo(() => {
    if (selectedSegment) {
      return (
        <span>
          Use {isMac ? 'Cmd' : 'Ctrl'} +{' '}
          <FontAwesomeIcon
            name="arrow-pointer"
            size="0.75rem"
            sx={{ color: '#88b8f6', padding: 0 }}
          />{' '}
          to select another segment
        </span>
      );
    }
    return INFO_TXT;
  }, [selectedSegment]);

  const routeLength = useMemo<number | null>(() => {
    if (viewOnly) return scenarioLength;
    if (!selectedSegment) return 0;
    const len = selectionFeature?.properties?.route_length;
    if (typeof len === 'number') return len;
    return null;
  }, [selectionFeature, selectedSegment, viewOnly, scenarioLength]);

  const routeLengthText = useMemo<string | null>(() => {
    if (!userProject) return null;
    return routeLength !== null
      ? formatDistance(routeLength, userProject.uses_metric)
      : null;
  }, [routeLength, userProject?.uses_metric]);

  const collapseId = 'detour-collapse';

  const geometry = !viewOnly
    ? selectionFeature?.geometry
    : scenarioRouteData?.feature?.geometry;

  return (
    <div
      className={classNames(
        mapStyles.map_tooltip_container,
        mapStyles.segments_tooltip_container,
      )}
    >
      <div>
        <Stack className={styles.header}>
          <Typography variant="h3">Define Closed Segments</Typography>
          <IconButton
            edge="end"
            disableRipple
            aria-label="Hide/Expand"
            title="Hide/Expand"
            className={styles.expand}
            aria-controls={collapseId}
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? (
              <KeyboardArrowDownIcon fontSize="medium" />
            ) : (
              <KeyboardArrowRightIcon fontSize="medium" />
            )}
          </IconButton>
        </Stack>
        <Stack className={styles.header}>
          <Typography variant="caption">Total closed distance</Typography>
          <div>
            {routeLengthText ? (
              <Typography variant="caption">{routeLengthText}</Typography>
            ) : (
              <LoadingDotAnimation sx={{ fontSize: '1rem' }} />
            )}
          </div>
        </Stack>
      </div>
      <Collapse
        unmountOnExit
        id={collapseId}
        in={isOpen}
        timeout="auto"
        classes={{ wrapperInner: styles.collapseContent }}
      >
        {!viewOnly && <InfoAlert txt={infoTxt} />}
        {(selectedSegment || viewOnly) && (
          <>
            <SelectedSegmentBar geometry={geometry} />
            {!viewOnly && <UndoSpan />}
          </>
        )}
      </Collapse>
    </div>
  );
}

function SelectedSegmentBar({ geometry }: { geometry: Geometry }) {
  const dispatch = useAppDispatch();
  const { layer } = useContext(LayerContext);

  const selectedSegmentId = useAppSelector(selectSegmentId);
  const scenarioSegments = useAppSelector(selectScenarioSegments);

  const [segmentName, setSegmentName] = useState<string | undefined>(undefined);

  const segmentId =
    selectedSegmentId || scenarioSegments[scenarioSegments.length - 1];

  const { currentData: segmentData } = useGetSegmentInfoQuery(
    { layerId: layer, routeIds: [segmentId] },
    { skip: !layer || !segmentId },
  );

  useEffect(() => {
    const newName = segmentData?.segments?.[segmentId]?.properties?.name;

    if (newName != null) setSegmentName(newName);
  }, [segmentData, segmentId]);

  const clearRoute = () => {
    dispatch(setRouteSelection(undefined));
    dispatch(setSelectedSegmentId(undefined));
  };

  return (
    <Box className={styles.container} title={segmentName}>
      <Typography className={styles.segmentName} variant="body2" noWrap>
        {segmentName}
      </Typography>
      <Box display="flex">
        <ZoomToRoute
          className={styles.zoomToRoute}
          geometry={geometry}
          size="12px"
        />
        {selectedSegmentId && (
          <IconButton
            title="Clear"
            aria-label="Clear selected segment"
            onClick={clearRoute}
            className={styles.clearButton}
          >
            <FontAwesomeIcon
              name="xmark-large"
              size="0.75rem"
              type="sharp-solid"
            />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
